import { Alert, Box, Button, Typography } from "@mui/material";
import { Add, CreditCard, FilterList } from "@mui/icons-material";
import { useState } from "react";

import useCreditTransactions from "../../services/useCreditTransactions";
import useCreateCreditTransaction from "../../services/useCreateCreditTransaction";
import useUpdateCreditTransaction from "../../services/useUpdateCreditTransaction";
import useDeleteCreditTransaction from "../../services/useDeleteCreditTransaction";
import {
  ICreditTransaction,
  ICreditTransactionFilters,
} from "../../types/creditTransaction";

import TransactionFilters from "./TransactionFilters";
import TransactionsTable from "./TransactionsTable";
import TransactionDialog, { ITransactionFormData } from "./TransactionDialog";

const INITIAL_FORM_DATA: ITransactionFormData = {
  urban_partner_id: null,
  amount: "",
  description: "",
};

const CreditTransactionsPage = () => {
  const [filters, setFilters] = useState<ICreditTransactionFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  const { data: transactionsData, isLoading } = useCreditTransactions(filters);
  const createMutation = useCreateCreditTransaction();
  const updateMutation = useUpdateCreditTransaction();
  const deleteMutation = useDeleteCreditTransaction();

  const [openDialog, setOpenDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] =
    useState<ICreditTransaction | null>(null);
  const [formData, setFormData] =
    useState<ITransactionFormData>(INITIAL_FORM_DATA);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const transactions = transactionsData?.results || [];

  const handleOpenDialog = (transaction?: ICreditTransaction) => {
    if (transaction) {
      setEditingTransaction(transaction);
      setFormData({
        urban_partner_id: transaction.urban_partner_id,
        amount: transaction.amount,
        description: transaction.description,
      });
    } else {
      setEditingTransaction(null);
      setFormData(INITIAL_FORM_DATA);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTransaction(null);
    setFormData(INITIAL_FORM_DATA);
  };

  const handleSave = () => {
    if (!editingTransaction && !formData.urban_partner_id) {
      setMessage({ type: "error", text: "Selecione uma parceira urbana." });
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setMessage({
        type: "error",
        text: "O valor deve ser maior que zero.",
      });
      return;
    }

    if (editingTransaction) {
      updateMutation.mutate(
        {
          id: editingTransaction.id,
          amount: formData.amount,
          description: formData.description,
        },
        {
          onSuccess: () => {
            setMessage({
              type: "success",
              text: "Transação atualizada com sucesso!",
            });
            handleCloseDialog();
          },
          onError: () => {
            setMessage({
              type: "error",
              text: "Erro ao atualizar transação.",
            });
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          urban_partner_id: formData.urban_partner_id!,
          amount: formData.amount,
          description: formData.description,
        },
        {
          onSuccess: () => {
            setMessage({
              type: "success",
              text: "Pré-pagamento registrado com sucesso!",
            });
            handleCloseDialog();
          },
          onError: () => {
            setMessage({
              type: "error",
              text: "Erro ao registrar pré-pagamento.",
            });
          },
        },
      );
    }
  };

  const handleDelete = (transaction: ICreditTransaction) => {
    if (
      window.confirm(
        `Tem certeza que deseja excluir esta transação de R$ ${parseFloat(transaction.amount).toFixed(2)} para ${transaction.urban_partner_name}?`,
      )
    ) {
      deleteMutation.mutate(transaction.id, {
        onSuccess: () => {
          setMessage({
            type: "success",
            text: "Transação excluída com sucesso!",
          });
        },
        onError: () => {
          setMessage({
            type: "error",
            text: "Erro ao excluir transação.",
          });
        },
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CreditCard sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography component="h1" variant="h5" fontWeight="bold">
            Crédito Pré-Pago
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Novo Pré-Pagamento
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {showFilters && (
        <TransactionFilters filters={filters} onFiltersChange={setFilters} />
      )}

      <TransactionsTable
        transactions={transactions}
        isLoading={isLoading}
        onEdit={handleOpenDialog}
        onDelete={handleDelete}
        onCreateNew={() => handleOpenDialog()}
      />

      <TransactionDialog
        open={openDialog}
        editingTransaction={editingTransaction}
        formData={formData}
        isLoading={createMutation.isLoading || updateMutation.isLoading}
        onClose={handleCloseDialog}
        onSave={handleSave}
        onFormDataChange={setFormData}
      />
    </Box>
  );
};

export default CreditTransactionsPage;
